/**
 * Phase 1 — contextual Pro gate (Anonymous → Free → Pro).
 *
 *   useStartTrial   — POST /billing/start-trial + refresh /me
 *   ProGateCard     — inline/contextual gate. Free user with unused
 *                     one-time trial → "Start 7-day trial"; otherwise
 *                     upgrade CTA (price from /billing/products).
 *   ProGatePage     — full-page variant used by requirePro routes.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock } from "lucide-react";
import { apiClient, formatApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { usePricing } from "../../hooks/usePricing";
import { trackFunnel } from "../../lib/funnel";
import { ProUpgradeButton } from "./PaywallComponents";

export function useStartTrial() {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const start = useCallback(
    async (context) => {
      if (busy) return false;
      setBusy(true);
      setError(null);
      try {
        await apiClient.post("/billing/start-trial", { context });
        await refreshUser();
        return true;
      } catch (err) {
        setError(formatApiError(err));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, refreshUser],
  );
  return { start, busy, error };
}

export function ProGateCard({
  surface = "generic",
  context = "pro_gate",
  lockedCount = null,
  compact = false,
  onUnlocked,
}) {
  const { t } = useTranslation("discover");
  const { user } = useAuth();
  const navigate = useNavigate();
  const { display: priceDisplay } = usePricing();
  const { start, busy, error } = useStartTrial();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!firedRef.current) {
      firedRef.current = true;
      trackFunnel("pro_gate_view", { surface });
    }
  }, [surface]);

  const onStartTrial = async () => {
    const ok = await start(context);
    if (ok && onUnlocked) onUnlocked();
  };

  const goRegister = () => {
    trackFunnel("register_cta", { surface: `pro_gate_${surface}` });
    navigate("/register");
  };

  return (
    <div
      data-testid={`pro-gate-${surface}`}
      className={`rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] ${
        compact ? "px-4 py-3.5 space-y-2.5" : "px-5 py-5 space-y-3.5"
      }`}
    >
      <div className="flex items-start gap-2.5">
        <Lock className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" aria-hidden />
        <div className="space-y-1">
          <div className="text-sm font-semibold text-zinc-100">
            {lockedCount
              ? t("gate.lockedCount", { count: lockedCount })
              : t(`gate.titles.${context}`, { defaultValue: t("gate.title") })}
          </div>
          <div className="text-xs text-zinc-400">
            {t(`gate.subs.${context}`, { defaultValue: t("gate.sub") })}
          </div>
        </div>
      </div>
      {!user ? (
        <button
          type="button"
          data-testid={`pro-gate-register-btn-${surface}`}
          onClick={goRegister}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 transition"
        >
          {t("gate.registerCta")}
        </button>
      ) : user.trial_available ? (
        <div className="space-y-1.5">
          <button
            type="button"
            data-testid={`pro-gate-trial-btn-${surface}`}
            disabled={busy}
            onClick={onStartTrial}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-60 transition"
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            {busy ? t("gate.trialStarting") : t("gate.trialCta")}
          </button>
          <div className="text-[11px] text-zinc-500">{t("gate.trialNote")}</div>
          {error && (
            <div className="text-[11px] text-rose-300" data-testid="pro-gate-error">
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <ProUpgradeButton data-testid={`pro-gate-upgrade-btn-${surface}`}>
            {t("gate.upgradeCta", { price: priceDisplay })}
          </ProUpgradeButton>
          <div className="text-[11px] text-zinc-500">{t("gate.upgradeNote")}</div>
        </div>
      )}
    </div>
  );
}

export function ProGatePage({ surface = "route", context = "route" }) {
  const { t } = useTranslation("discover");
  const navigate = useNavigate();
  return (
    <div
      data-testid="pro-gate-page"
      className="mx-auto flex min-h-[60vh] max-w-lg flex-col justify-center gap-4 px-4"
    >
      <h1 className="text-2xl font-semibold text-zinc-50">
        {t(`gate.titles.${context}`, { defaultValue: t("gate.titles.route") })}
      </h1>
      <ProGateCard surface={surface} context={context} />
      <button
        type="button"
        data-testid="pro-gate-back-btn"
        onClick={() => navigate(-1)}
        className="self-start text-xs text-zinc-400 underline-offset-4 hover:text-zinc-200 hover:underline"
      >
        {t("gate.back")}
      </button>
    </div>
  );
}
